import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import {
  InformationCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import Select from 'react-select';
import { z } from 'zod';
import { AddressAutocomplete } from '../../../components/address-autocomplete';
import { FieldGroup, Fieldset } from '../../../components/fieldset';
import { Heading } from '../../../components/heading';
import { Input } from '../../../components/input';
import { Text } from '../../../components/text';
import { type Step } from './StepNavigation';
import { BasicInfoFormData, Option } from './types';
import {
  BrandLegend,
  onboardingStepHasVisibleWarnings,
  reactSelectReset,
  StepHeaderStatusBanner,
  ValidationMessage,
} from './utils';

type StateSelectProps = {
  formData: {
    state: string;
  };
  handleChange: (e: { target: { name: string; value: string } }) => void;
  stateOptions: Option[];
};

export const StateSelect = ({ formData, handleChange, stateOptions }: StateSelectProps) => {
  const handleStateChange = (selectedOption: Option | null) => {
    handleChange({
      target: {
        name: 'state',
        value: selectedOption?.value || '',
      },
    });
  };

  return (
    <div data-testid="onboarding-basic-info-state">
      <Select
        name="state"
        options={stateOptions}
        value={stateOptions.find((option) => option.value === formData.state)}
        onChange={handleStateChange}
        getOptionLabel={(e) => e.value}
        getOptionValue={(e) => e.value}
        styles={reactSelectReset}
        placeholder="State"
      />
    </div>
  );
};

interface BasicInfoProps {
  stepInfo: Step;
  initialData: BasicInfoFormData | undefined;
  registrationSteps?: string[];
  stateOptions: Option[];
  nppesData?: {
    addresses: Array<{
      city: string;
      state: string;
      address_1: string;
      postal_code: string;
      address_purpose: string;
    }>;
  };
  showFatalErrors?: boolean;
}

export interface BasicInfoRef {
  formData: BasicInfoFormData;
  hasFatalErrors: boolean;
  validationMessages: Record<
    string,
    { message: string; icon: React.ReactNode; colorClass: string }
  >;
}

export const basicInfoValidationSchema = z.object({
  firstName: z.string().superRefine((val, ctx) => {
    if (val.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'First Name is required',
        fatal: true,
      });
    }
  }),
  lastName: z.string().superRefine((val, ctx) => {
    if (val.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Last Name is required',
        fatal: true,
      });
    }
  }),
  dateOfBirth: z.string().superRefine((val, ctx) => {
    if (val.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please enter your date of birth',
        fatal: false,
      });
      return;
    }

    // Parse date parts manually
    const parts = val.replace(/[-./]/g, '/').split('/');

    // Ensure we have exactly 3 parts
    if (parts.length !== 3) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please enter a valid date (mm/dd/yyyy)',
        fatal: true,
      });
      return;
    }

    const month = parseInt(parts[0], 10);
    const day = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);

    // Basic date validation
    const isValidDate =
      !isNaN(month) &&
      !isNaN(day) &&
      !isNaN(year) &&
      month > 0 &&
      month <= 12 &&
      day > 0 &&
      day <= 31 &&
      year > 1900 &&
      year < new Date().getFullYear();

    if (!isValidDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please enter a valid date (mm/dd/yyyy)',
        fatal: true,
      });
    }
  }),
  npiNumber: z.string().superRefine((val, ctx) => {
    if (val.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please enter your NPI number',
        fatal: false,
      });
    } else if (!val.match(/^\d{10}$/)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'NPI must be exactly 10 digits',
        fatal: true,
      });
    }
  }),
  streetAddress: z.string().superRefine((val, ctx) => {
    if (val.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please enter your street address',
        fatal: false,
      });
    }
  }),
  city: z.string().superRefine((val, ctx) => {
    if (val.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please enter your city',
        fatal: false,
      });
    }
  }),
  state: z.string().superRefine((val, ctx) => {
    if (val.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please enter your state',
        fatal: false,
      });
    }
  }),
  zip: z.string().superRefine((val, ctx) => {
    if (val.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please enter your ZIP code',
        fatal: false,
      });
    } else if (!val.match(/^\d{5}$/)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'ZIP code must be exactly 5 digits',
        fatal: true,
      });
    }
  }),
});

export const BasicInfo = forwardRef<BasicInfoRef, BasicInfoProps>(
  (
    { stepInfo, initialData, registrationSteps, stateOptions, nppesData, showFatalErrors = false },
    ref
  ) => {
    const [formData, setFormData] = useState<BasicInfoFormData>(
      initialData || {
        firstName: '',
        lastName: '',
        dateOfBirth: '',
        npiNumber: '',
        streetAddress: '',
        streetAddress2: '',
        city: '',
        state: '',
        zip: '',
      }
    );
    const [validationMessages, setValidationMessages] = useState<
      Record<string, { message: string; icon: React.ReactNode; colorClass: string }>
    >({});
    const [hasFatalErrors, setHasFatalErrors] = useState(false);

    useImperativeHandle(ref, () => ({
      formData,
      hasFatalErrors,
      validationMessages,
    }));

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value, type, checked } = e.target;

      setFormData((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    };

    const handleAddressAutocomplete = (addressComponents: {
      streetAddress: string;
      streetAddress2?: string;
      city: string;
      state: string;
      zipCode: string;
    }) => {
      setFormData((prev) => ({
        ...prev,
        streetAddress: addressComponents.streetAddress,
        streetAddress2: addressComponents.streetAddress2,
        city: addressComponents.city,
        state: addressComponents.state,
        zip: addressComponents.zipCode,
      }));
    };

    // eslint-disable-next-line sonarjs/cognitive-complexity
    useEffect(() => {
      // Pre-populate address fields if they're all empty and we have NPPES data
      if (
        nppesData?.addresses &&
        !formData.streetAddress &&
        !formData.city &&
        !formData.state &&
        !formData.zip
      ) {
        const mailingAddress = nppesData.addresses.find(
          (addr) => addr.address_purpose === 'MAILING'
        );
        if (mailingAddress) {
          setFormData((prev) => ({
            ...prev,
            streetAddress: mailingAddress.address_1 || '',
            city: mailingAddress.city || '',
            state: mailingAddress.state || '',
            zip: (mailingAddress.postal_code || '').slice(0, 5),
          }));
        }
      }

      const newValidationMessages: Record<
        string,
        { message: string; icon: React.ReactNode; colorClass: string }
      > = {};
      let hasFatal = false;

      const result = basicInfoValidationSchema.safeParse(formData);
      if (!result.success) {
        result.error.errors.forEach((err) => {
          const field = err.path[0];
          // Show fatal errors when showFatalErrors is true, show non-fatal errors if step has been visited
          if (
            (showFatalErrors && err.fatal) ||
            (registrationSteps?.includes('BASIC_INFO') && !err.fatal)
          ) {
            newValidationMessages[field] = {
              message: err.fatal ? err.message : 'Missing info',
              icon: err.fatal ? (
                <XCircleIcon className="tw:h-5 tw:w-5" />
              ) : (
                <ExclamationTriangleIcon className="tw:h-5 tw:w-5" />
              ),
              // TODO: our normal warning color did not seem to call enough attention to fields that needed to be completed
              colorClass: 'tw:text-stoplight-danger',
            };
          }
          if (err.fatal) {
            hasFatal = true;
          }
        });
      }
      setValidationMessages(newValidationMessages);
      setHasFatalErrors(hasFatal);
    }, [formData, registrationSteps, showFatalErrors, nppesData]);

    const hasStepWarnings = onboardingStepHasVisibleWarnings(validationMessages);
    const showStepComplete =
      Boolean(registrationSteps?.includes('BASIC_INFO')) &&
      !hasStepWarnings &&
      basicInfoValidationSchema.safeParse(formData).success;

    return (
      <div className="tw:flex tw:flex-col tw:p-4 tw:lg:px-0 " data-testid="onboarding-basic-info">
        <div className="tw:border-b tw:border-zinc-200 tw:mb-4">
          <div className="tw:flex tw:flex-col tw:gap-3 tw:sm:flex-row tw:sm:items-start tw:sm:justify-between">
            <div className="tw:min-w-0 tw:flex-1">
              <Heading className="tw:!mb-0 tw:dark:!text-zinc-300">{stepInfo.label}</Heading>
              <Text className="tw:pb-2 tw:!mb-0">{stepInfo.description}</Text>
            </div>
            <StepHeaderStatusBanner hasWarnings={hasStepWarnings} showComplete={showStepComplete} />
          </div>
        </div>

        <form id="basicInfoForm">
          {/* Personal Information Group */}
          <Fieldset className="tw:!mb-6">
            <BrandLegend>Personal Information</BrandLegend>
            <Text variant="small">
              Sendit hides your name from all healthcare organizations until you connect with them.
              You can change this in your profile at any time.
            </Text>
            <FieldGroup>
              <div className="tw:mb-4">
                <div className="tw:grid tw:grid-cols-2 tw:gap-4">
                  <div>
                    <Input
                      type="text"
                      id="firstName"
                      name="firstName"
                      placeholder="First Name"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                    />
                    <ValidationMessage
                      message={validationMessages?.firstName?.message}
                      icon={validationMessages?.firstName?.icon}
                      colorClass={validationMessages?.firstName?.colorClass}
                    />
                  </div>
                  <div>
                    <Input
                      type="text"
                      id="lastName"
                      name="lastName"
                      placeholder="Last Name"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                    />
                    <ValidationMessage
                      message={validationMessages?.lastName?.message}
                      icon={validationMessages?.lastName?.icon}
                      colorClass={validationMessages?.lastName?.colorClass}
                    />
                  </div>
                </div>
              </div>
              <div className="tw:mb-4">
                <div className="tw:relative tw:block tw:w-full">
                  <Input
                    type="text"
                    id="dateOfBirth"
                    name="dateOfBirth"
                    placeholder="Date of Birth (mm/dd/yyyy)"
                    value={formData.dateOfBirth}
                    onChange={handleChange}
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus={true}
                  />
                  <ValidationMessage
                    message={validationMessages?.dateOfBirth?.message}
                    icon={validationMessages?.dateOfBirth?.icon}
                    colorClass={validationMessages?.dateOfBirth?.colorClass}
                  />
                </div>
              </div>
              <div>
                <div className="tw:relative tw:flex tw:items-center">
                  <Input
                    type="text"
                    id="npiNumber"
                    name="npiNumber"
                    placeholder="NPI Number"
                    value={formData.npiNumber}
                    onChange={handleChange}
                    required
                  />
                  <Popover className="tw:absolute tw:right-2">
                    <PopoverButton className="tw:focus:outline-none tw:pt-1">
                      <InformationCircleIcon className="tw:h-5 tw:w-5 tw:text-zinc-500 tw:hover:text-zinc-600" />
                    </PopoverButton>
                    <PopoverPanel className="tw:absolute tw:z-10 tw:w-72 tw:px-4 tw:py-2 tw:right-0 tw:-mt-2 tw:bottom-6 tw:text-sm tw:bg-white tw:border tw:border-zinc-200 tw:rounded-md tw:shadow-lg tw:dark:bg-zinc-600 tw:dark:border-zinc-700 tw:dark:text-white/90">
                      We use this information to verify your identity
                    </PopoverPanel>
                  </Popover>
                </div>
                <ValidationMessage
                  message={validationMessages?.npiNumber?.message}
                  icon={validationMessages?.npiNumber?.icon}
                  colorClass={validationMessages?.npiNumber?.colorClass}
                />
              </div>
            </FieldGroup>
          </Fieldset>

          {/* Location Group */}
          <Fieldset className="tw:mb-6">
            <BrandLegend>Home Address</BrandLegend>
            <FieldGroup>
              <div className="tw:mb-4">
                <AddressAutocomplete
                  id="streetAddress"
                  inputTestId="onboarding-basic-info-street"
                  value={formData.streetAddress}
                  onChange={(value) =>
                    handleChange({
                      target: { name: 'streetAddress', value },
                    } as React.ChangeEvent<HTMLInputElement>)
                  }
                  onPlaceSelected={handleAddressAutocomplete}
                  placeholder="Street Address"
                />
                <ValidationMessage
                  message={validationMessages?.streetAddress?.message}
                  icon={validationMessages?.streetAddress?.icon}
                  colorClass={validationMessages?.streetAddress?.colorClass}
                />
              </div>
              <div className="tw:mb-4">
                <Input
                  type="text"
                  id="streetAddress2"
                  name="streetAddress2"
                  placeholder="Apartment, Suite, Unit, etc. (optional)"
                  value={formData.streetAddress2 || ''}
                  onChange={handleChange}
                />
              </div>
              <div className="tw:mb-4">
                <Input
                  type="text"
                  id="city"
                  name="city"
                  placeholder="City"
                  value={formData.city}
                  onChange={handleChange}
                  required
                />
                <ValidationMessage
                  message={validationMessages?.city?.message}
                  icon={validationMessages?.city?.icon}
                  colorClass={validationMessages?.city?.colorClass}
                />
              </div>
              <div className="tw:grid tw:grid-cols-2 tw:gap-4">
                <div>
                  <StateSelect
                    formData={formData}
                    handleChange={handleChange}
                    stateOptions={stateOptions}
                  />
                  <ValidationMessage
                    message={validationMessages?.state?.message}
                    icon={validationMessages?.state?.icon}
                    colorClass={validationMessages?.state?.colorClass}
                  />
                </div>
                <div>
                  <Input
                    type="text"
                    id="zip"
                    name="zip"
                    placeholder="ZIP Code"
                    value={formData.zip}
                    onChange={handleChange}
                    required
                  />
                  <ValidationMessage
                    message={validationMessages?.zip?.message}
                    icon={validationMessages?.zip?.icon}
                    colorClass={validationMessages?.zip?.colorClass}
                  />
                </div>
              </div>
            </FieldGroup>
          </Fieldset>

          <div className="tw:flex tw:justify-end">
            <button type="submit" className="tw:hidden">
              Submit
            </button>
          </div>
        </form>
      </div>
    );
  }
);
BasicInfo.displayName = 'BasicInfo';
